import { useCallback, useMemo } from 'react'

import { useDashboardModals } from 'contexts/DashboardModalsContext'
import { useDashboardsQueries } from 'hooks-queries/dashboards'
import { useTranslate } from 'lib/tolgee'
import { translations as translationsCommon } from 'modules/dashboard/common/dashboard.i18n'
import { PanelStore, UserStore } from 'stores'
import { analyticsTracker } from 'utils'
import { getRoute } from 'utils/routes'
import { PATHNAMES } from 'utils/routes/consts'

import { BASE_PAGE_SIZE, EDITOR_INITIAL_PAGE, LOAD_DASHBOARD_FOLDERS_LOG } from './dashboard-folder-list.consts'
import type { IDashboardFolderList, TFolder } from './dashboard-folder-list.types'

export const useDashboardFolderListModel = (): IDashboardFolderList => {
  const { setIsCreateDashboardFolderOpen, setIsDeleteDashboardFolderOpen, setFolder, setIsEditDashboardFolderOpen } =
    useDashboardModals()
  const t = useTranslate()

  const panelId = Object(PanelStore?.selectedPanel)?.panelId
  const isUserViewer = UserStore.getRole === 'viewer'
  const initialPageSize = isUserViewer ? BASE_PAGE_SIZE : EDITOR_INITIAL_PAGE

  const { data, loading, fetchMore } = useDashboardsQueries({
    queryName: 'getJobExternalFolders',
    errorLog: LOAD_DASHBOARD_FOLDERS_LOG,
    queryVariables: {
      pagination: {
        take: initialPageSize,
      },
    },
    options: {
      fetchPolicy: 'network-only',
    },
  })

  const getDashboardFolderOptions = useCallback(
    (folder: { id: string; name: string }) => {
      if (isUserViewer) {
        return []
      }

      const handleOpenFolderModal = (action: 'delete' | 'edit') => {
        setFolder(folder)
        const { DASHBOARD_FOLDERS } = analyticsTracker.eventNames

        if (action === 'delete') {
          analyticsTracker.eventTrack(DASHBOARD_FOLDERS.OPEN_DELETE_FOLDER, {
            folderName: folder.name,
            folderId: folder.id,
          })
          setIsDeleteDashboardFolderOpen(true)
        }
        if (action === 'edit') {
          analyticsTracker.eventTrack(DASHBOARD_FOLDERS.OPEN_EDIT_FOLDER, {
            folderName: folder.name,
            folderId: folder.id,
          })
          setIsEditDashboardFolderOpen(true)
        }
      }

      return [
        {
          label: t(translationsCommon.deleteDashboardFolder),
          onClick: () => handleOpenFolderModal('delete'),
        },
        {
          label: t(translationsCommon.editDashboardFolder),
          onClick: () => handleOpenFolderModal('edit'),
        },
      ]
    },
    [isUserViewer, setFolder, setIsDeleteDashboardFolderOpen, setIsEditDashboardFolderOpen, t]
  )

  const foldersList = useMemo(() => {
    return (
      data?.listWorkspaceJobExternalFolders?.data?.map(item => {
        const link = getRoute(PATHNAMES.DASHBOARDS_FOLDER, { folderId: item.id, panelId })
        return {
          folderName: item.name,
          dashboardCount: item.jobsCount,
          id: item.id,
          dashboardFolderOptions: getDashboardFolderOptions(item),
          link,
        }
      }) || []
    )
  }, [data?.listWorkspaceJobExternalFolders?.data, getDashboardFolderOptions, panelId])

  const hasMore = useMemo(() => {
    return Boolean(data?.listWorkspaceJobExternalFolders?.cursor)
  }, [data?.listWorkspaceJobExternalFolders?.cursor])

  const loadMore = async () => {
    const cursor = data?.listWorkspaceJobExternalFolders?.cursor
    if (loading || !hasMore) return
    try {
      await fetchMore({
        variables: {
          pagination: {
            take: BASE_PAGE_SIZE,
            cursor,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult.listWorkspaceJobExternalFolders) {
            return prev
          }

          return Object.assign({}, prev, {
            listWorkspaceJobExternalFolders: {
              ...fetchMoreResult.listWorkspaceJobExternalFolders,
              data: [
                ...(prev.listWorkspaceJobExternalFolders?.data || []),
                ...(fetchMoreResult?.listWorkspaceJobExternalFolders?.data || []),
              ],
            },
          })
        },
      })
    } catch {
      // Errors are reported centrally by the Apollo ErrorLink.
    }
  }

  const handleCreateFolder = () => {
    setIsCreateDashboardFolderOpen(true)
  }

  const handleTrackClick = (folder: TFolder) => {
    const { DASHBOARD_FOLDERS } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(DASHBOARD_FOLDERS.CLICK_FOLDER, {
      folderName: folder.folderName,
      folderId: folder.id,
      dashboardCount: folder.dashboardCount,
    })
  }

  const shouldRenderCreateFolderButton = !isUserViewer

  const isListEmpty = !foldersList.length && !loading

  const shouldNotRender = isUserViewer && isListEmpty && !loading

  return {
    foldersList,
    isLoadingFolders: loading,
    shouldRenderCreateFolderButton,
    shouldRenderLoadMore: hasMore,
    loadMore,
    isListEmpty,
    shouldNotRender,
    handleCreateFolder,
    handleTrackClick,
  }
}
